import React, { useEffect, useState } from 'react';
import { MessageCircle, X, Sparkles } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { trackWhatsAppClick, trackConsultationCTAClick, trackEvent } from '../utils/analytics';

/**
 * StickyMobileCTA — mobile-only (<768px) bottom bar that appears after the
 * user scrolls past 300px. Context-aware CTAs:
 *  - On /poocho-guruji, /faq*, /booking?tier=quick_99 → "₹99 में उत्तर पाएँ →"
 *    routes to /poocho-guruji#poocho-question-picker (fastest Rs.99 revenue path)
 *  - Elsewhere → WhatsApp + Book Now (existing behaviour)
 *
 * Wired globally from App.js so it appears on every public route.
 *
 * The Hindi WhatsApp prefill is intentionally warm + authority-forward
 * ("नमस्ते आचार्य जी, मुझे परामर्श चाहिए।") per the Feb 08, 2026 CRO spec.
 */
const WA_PHONE = '919311369729';
const WA_PREFILL_HI = 'नमस्ते आचार्य जी, मुझे परामर्श चाहिए।';
const WA_HREF = `https://wa.me/${WA_PHONE}?text=${encodeURIComponent(WA_PREFILL_HI)}`;

const DISMISS_KEY = 'va_sticky_mobile_cta_dismissed';

// Routes where the sticky CTA should surface the ₹99 Poocho Guruji offer
const isPoochoContext = (pathname, search) => {
  if (!pathname) return false;
  if (pathname.startsWith('/poocho-guruji')) return true;
  if (pathname.startsWith('/faq')) return true;
  if (pathname.startsWith('/booking') && (search || '').includes('tier=quick_99')) return true;
  return false;
};

export default function StickyMobileCTA() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const { pathname, search } = useLocation();
  const poochoMode = isPoochoContext(pathname, search);

  // Respect a prior dismiss (session-scoped — resets on new tab/session)
  useEffect(() => {
    try {
      if (window.sessionStorage.getItem(DISMISS_KEY) === '1') setDismissed(true);
    } catch (_) { /* private-mode: ignore */ }
  }, []);

  // Show after 300px scroll — throttled with rAF for smoothness
  useEffect(() => {
    if (dismissed) return;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        setVisible(window.scrollY > 300);
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [dismissed]);

  const dismiss = () => {
    setDismissed(true);
    try { window.sessionStorage.setItem(DISMISS_KEY, '1'); } catch (_) { /* ignore */ }
  };

  const onWhatsAppClick = () => {
    try { trackWhatsAppClick('sticky_mobile_cta'); } catch (_) { /* ignore */ }
  };

  const onBookClick = () => {
    try { trackConsultationCTAClick('sticky_mobile_cta'); } catch (_) { /* ignore */ }
  };

  const onPoochoClick = () => {
    try {
      trackEvent('poocho_sticky_cta_click', {
        source: 'sticky_mobile_cta',
        path: pathname,
      });
    } catch (_) { /* ignore */ }
  };

  if (dismissed || !visible) return null;

  // ═══ Poocho Guruji context — single ₹99 CTA (max focus, min distraction) ═══
  if (poochoMode) {
    return (
      <div
        className="md:hidden fixed bottom-0 left-0 right-0 z-50"
        role="region"
        aria-label="Poocho Guruji ₹99 quick answer"
        data-testid="sticky-mobile-cta"
      >
        <div
          className="mx-2 mb-2 rounded-2xl bg-[#1a0710]/95 backdrop-blur-md border border-gold/50 shadow-2xl shadow-black/60 flex items-stretch gap-2 p-2"
          style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
        >
          <a
            href="/poocho-guruji#poocho-question-picker"
            onClick={onPoochoClick}
            className="flex-1 flex items-center justify-center gap-2 rounded-xl font-bold text-purple-950 text-sm py-3 px-3 shadow-md active:scale-[0.98] transition-transform"
            style={{ background: 'linear-gradient(90deg,#F4D477,#D4AF37,#B8942D)' }}
            data-testid="sticky-mobile-poocho-btn"
            aria-label="₹99 में उत्तर पाएँ"
          >
            <Sparkles className="w-4 h-4" />
            ₹99 में उत्तर पाएँ →
          </a>
          <button
            onClick={dismiss}
            className="w-10 flex items-center justify-center rounded-xl text-text-muted hover:text-white bg-white/5 hover:bg-white/10 transition-colors"
            aria-label="Close"
            data-testid="sticky-mobile-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="md:hidden fixed bottom-0 left-0 right-0 z-50"
      role="region"
      aria-label="Quick consultation actions"
      data-testid="sticky-mobile-cta"
    >
      <div
        className="mx-2 mb-2 rounded-2xl bg-purple-950/95 backdrop-blur-md border border-gold/40 shadow-2xl shadow-black/60 flex items-stretch gap-2 p-2"
        style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
      >
        <a
          href={WA_HREF}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onWhatsAppClick}
          className="flex-1 flex items-center justify-center gap-2 rounded-xl font-bold text-white text-sm py-3 px-3 shadow-md active:scale-[0.98] transition-transform"
          style={{ background: '#25D366' }}
          data-testid="sticky-mobile-whatsapp-btn"
          aria-label="Chat on WhatsApp"
        >
          <MessageCircle className="w-4 h-4" />
          WhatsApp
        </a>
        <a
          href="/#book-consultation"
          onClick={onBookClick}
          className="flex-1 flex items-center justify-center gap-2 rounded-xl font-bold text-white text-sm py-3 px-3 shadow-md active:scale-[0.98] transition-transform"
          style={{ background: 'linear-gradient(135deg, #F97316, #EA580C)' }}
          data-testid="sticky-mobile-book-btn"
          aria-label="Book consultation"
        >
          अभी बुक करें
        </a>
        <button
          onClick={dismiss}
          className="w-10 flex items-center justify-center rounded-xl text-text-muted hover:text-white bg-white/5 hover:bg-white/10 transition-colors"
          aria-label="Close"
          data-testid="sticky-mobile-close-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

